'use client'

import { Box, Grid2 } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import { Fragment, memo, ReactElement, useRef } from 'react'
import { TransitionGroup } from 'react-transition-group'
import useMediaQueries from '../useMediaQueries'
import { FlatListProps, ItemWithId } from './types'
import useListKeyGenerator from './useListKeyGenerator'
import useListScrollTracking from './useListScrollTracking'

// TODO: Share this type with the other workspaces
// This takes some Props object and makes some of its properties required
// Example: TRequireCertainProps<{ foo?: string, bar?: number }, 'foo'> will give you { foo: string, bar?: number }
type RequireCertainProps<T, PropsToBeRequired extends keyof T> = Omit<T, PropsToBeRequired> &
    Required<Pick<T, PropsToBeRequired>>

type ListProps<Item extends ItemWithId> = Omit<
    FlatListProps<Item>,
    'ListHeaderComponent' | 'ListFooterComponent' | 'ListEmptyComponent' | 'listWrapperStyle' | 'columns'
> &
    RequireCertainProps<FlatListProps<Item>, 'columns'> & { isGrid: boolean | null }

type ListItemProps<Item extends ItemWithId> = Omit<ListProps<Item>, 'data' | 'keyExtractor'> & {
    item: Item
    index: number
    count: number
    lastItemRef: React.RefCallback<HTMLElement>
}

type NonGridListItemProps<Item extends ItemWithId> = RequireCertainProps<
    Pick<
        FlatListProps<Item>,
        'columns' | 'isFetching' | 'LoadingMoreComponent' | 'loadingMorePosition' | 'ItemSeparatorComponent'
    >,
    'columns'
> & {
    renderedItem: ReactElement | null
    isLast: boolean
    lastItemRef: React.RefCallback<HTMLElement>
}

type GridListItemProps<Item extends ItemWithId> = Omit<NonGridListItemProps<Item>, 'ItemSeparatorComponent'>

function GridListItem<Item extends ItemWithId>({
    columns,
    isLast,
    renderedItem,
    isFetching,
    lastItemRef,
    LoadingMoreComponent,
    loadingMorePosition,
}: GridListItemProps<Item>) {
    const ItemWrapperProps = {
        size: {
            sm: 12 / columns,
        },
        role: 'listitem',
    }

    if (isLast) {
        const loadMoreComponent =
            LoadingMoreComponent && isFetching && loadingMorePosition ? (
                <Grid2 {...ItemWrapperProps}>
                    <LoadingMoreComponent />
                </Grid2>
            ) : null

        return (
            <>
                {loadingMorePosition === 'top' ? loadMoreComponent : null}
                <Grid2 {...ItemWrapperProps} ref={lastItemRef}>
                    {renderedItem}
                </Grid2>
                {loadingMorePosition === 'bottom' ? loadMoreComponent : null}
            </>
        )
    }

    return <Grid2 {...ItemWrapperProps}>{renderedItem}</Grid2>
}

function NonGridListItem<Item extends ItemWithId>({
    isLast,
    renderedItem,
    isFetching,
    lastItemRef,
    ItemSeparatorComponent,
    LoadingMoreComponent,
    loadingMorePosition,
}: NonGridListItemProps<Item>) {
    if (isLast) {
        const loadMoreComponent =
            LoadingMoreComponent && isFetching && loadingMorePosition ? <LoadingMoreComponent /> : null

        return (
            <div role="listitem" ref={lastItemRef}>
                {loadingMorePosition === 'top' ? loadMoreComponent : null}
                {renderedItem}
                {loadingMorePosition === 'bottom' ? loadMoreComponent : null}
            </div>
        )
    }

    const separator = ItemSeparatorComponent && loadingMorePosition ? <ItemSeparatorComponent /> : null

    return (
        <div role="listitem">
            {loadingMorePosition === 'top' ? separator : null}
            {renderedItem}
            {loadingMorePosition === 'bottom' ? separator : null}
        </div>
    )
}

function ListItem<Item extends ItemWithId>({ isGrid, item, index, count, renderItem, ...props }: ListItemProps<Item>) {
    const isFirst = index === 0
    const isLast = index === count - 1
    const Root = isGrid ? GridListItem : NonGridListItem

    return <Root renderedItem={renderItem({ item, index }, { isFirst, isLast })} isLast={isLast} {...props} />
}

function RenderList<Item extends ItemWithId>({
    isGrid,
    data,
    keyExtractor,
    hasMoreData,
    isFetching,
    onScrollToEnd,
    scrollToEndThreshold,
    collapsible,
    ...listItemProps
}: ListProps<Item>) {
    const lastItemRef = useListScrollTracking({ isFetching, hasMoreData, onScrollToEnd, scrollToEndThreshold })
    const getKey = useListKeyGenerator<Item>(keyExtractor)

    if (isGrid === null) {
        return null
    }
    const ListRoot = collapsible ? TransitionGroup : Fragment
    const ItemRoot = collapsible ? Collapse : Fragment

    return (
        <ListRoot>
            {data.map((item, index) => (
                <ItemRoot key={`collapse-${getKey(item, index)}`}>
                    <ListItem
                        item={item}
                        index={index}
                        key={getKey(item, index)}
                        count={data.length}
                        isGrid={isGrid}
                        isFetching={isFetching}
                        lastItemRef={lastItemRef}
                        {...listItemProps}
                    />
                </ItemRoot>
            ))}
        </ListRoot>
    )
}

function Contents<Item extends ItemWithId>({
    data,
    columns,
    ListEmptyComponent = () => undefined,
    listWrapperStyle,
    collapsible,
    isGrid,
    ...listProps
}: Omit<FlatListProps<Item>, 'ListHeaderComponent' | 'ListFooterComponent' | 'columns' | 'sx'> & {
    isGrid: boolean
    columns: NonNullable<FlatListProps<Item>['columns']>
}): ReactElement | null {
    const listWrapperEl = useRef<HTMLDivElement>(null)

    if (!data || data.length === 0) {
        return <ListEmptyComponent />
    }

    const ListWrapperComponent = isGrid ? Grid2 : 'div'
    const ListWrapperProps = isGrid
        ? {
              container: true,
              spacing: 3,
          }
        : null

    return (
        <ListWrapperComponent role="list" style={listWrapperStyle} {...ListWrapperProps} ref={listWrapperEl}>
            <RenderList isGrid={isGrid} data={data} columns={columns} collapsible={collapsible} {...listProps} />
        </ListWrapperComponent>
    )
}

const memoWithGenericType: <T>(component: T) => T = memo

const FlatList = memoWithGenericType(function FlatList_<Item extends ItemWithId>({
    columns = 1,
    ListHeaderComponent = () => undefined,
    ListFooterComponent = () => undefined,
    sx,
    ...rest
}: FlatListProps<Item>): ReactElement | null {
    const { breakpointDependentValue } = useMediaQueries()
    const isGrid = breakpointDependentValue(false, columns > 1, null)

    if (isGrid === null) {
        return null
    }

    return (
        <Box component="section" sx={sx}>
            <ListHeaderComponent />
            <Contents isGrid={isGrid} columns={columns} {...rest} />
            <ListFooterComponent />
        </Box>
    )
})

export default FlatList
